/**
 * Nosto-PlpController.js
 *
 * Coordina la carga de productos de una colección mediante Nosto.
 * Administra paginación, ordenamiento, infinite scroll, restauración básica
 * desde URL y conexión con NostoClient, NostoFacetManager,
 * NostoHitStore y NostoProductResultsRenderer.
 *
 * Este archivo no renderiza directamente product cards ni administra
 * internamente el estado de los filtros.
 */

(function initializeNostoPlpController(global) {
    "use strict";

    class NostoPlpController {
        /**
         * Crea una instancia del controller de PLP.
         */
        constructor({
            categoryId = "",
            categoryPath = "",
            client,
            facetManager,
            hitStore,
            renderer,
            sortElement = null,
            loadMoreElement = null,
            pageSize = 12,
            productFields = [
                "name",
                "url",
                "imageUrl",
            ],
            track = "category",
            redirect = true,
            syncPaginationToUrl = true,
            loadedUrlParam = "loaded",
            syncSortToUrl = true,
            sortUrlParam = "sort",
            restoreLoadedProducts = true,
            restoreScroll = true,
            historyStateKey = "__nostoPlp",
            maxRestoreChunkSize = 60,
            infiniteScrollRootMargin = "3000px 0px",
            onFacets = null,
            onResponse = null,
            onError = null,
            onLoadingChange = null,
            debug = false,
        } = {}) {
            this.categoryId =
                this.normalizeString(categoryId);

            this.categoryPath =
                this.normalizeString(
                    categoryPath
                );

            this.client = client;
            this.facetManager = facetManager;
            this.hitStore = hitStore;
            this.renderer = renderer;

            this.sortElement =
                this.resolveElement(sortElement);

            this.loadMoreElement =
                this.resolveElement(loadMoreElement);

            this.pageSize =
                this.normalizePositiveInteger(
                    pageSize,
                    12
                );

            this.productFields =
                Array.isArray(productFields)
                    ? [...productFields]
                    : [];

            this.track =
                this.normalizeString(track) ||
                "category";

            this.redirect = Boolean(redirect);

            this.syncPaginationToUrl =
                Boolean(syncPaginationToUrl);

            this.loadedUrlParam =
                this.normalizeString(loadedUrlParam) ||
                "loaded";

            this.syncSortToUrl =
                Boolean(syncSortToUrl);

            this.sortUrlParam =
                this.normalizeString(sortUrlParam) ||
                "sort";

            this.restoreLoadedProducts =
                Boolean(restoreLoadedProducts);

            this.restoreScroll =
                Boolean(restoreScroll);

            this.historyStateKey =
                this.normalizeString(historyStateKey) ||
                "__nostoPlp";

            this.maxRestoreChunkSize =
                this.normalizePositiveInteger(
                    maxRestoreChunkSize,
                    60
                );

            /**
             * Anticipa la siguiente carga antes de que el usuario alcance
             * físicamente el final de los productos actuales.
             *
             * 900px equivale aproximadamente a una o dos filas,
             * dependiendo del viewport y la proporción de las cards.
             */
            this.infiniteScrollRootMargin =
                this.normalizeString(
                    infiniteScrollRootMargin
                ) || "900px 0px";

            this.lastScrollY =
                global.scrollY || 0;

            this.boundHandleInfiniteScrollFallback =
                global.theme?.throttle
                    ? global.theme.throttle(
                        this.handleInfiniteScrollFallback.bind(this),
                        100
                    )
                    : this.handleInfiniteScrollFallback.bind(this);

            this.onFacets =
                typeof onFacets === "function"
                    ? onFacets
                    : null;

            this.onResponse =
                typeof onResponse === "function"
                    ? onResponse
                    : null;

            this.onError =
                typeof onError === "function"
                    ? onError
                    : null;

            this.onLoadingChange =
                typeof onLoadingChange === "function"
                    ? onLoadingChange
                    : null;

            this.debug = Boolean(debug);

            this.offset = 0;
            this.total = null;
            this.hasMore = true;
            this.isLoading = false;
            this.loadingMode = "replace";
            this.isInitialized = false;
            this.isRestoring = false;

            this.latestRequestId = 0;
            this.observer = null;

            this.boundHandleSortChange =
                this.handleSortChange.bind(this);

            this.boundHandlePageHide =
                this.handlePageHide.bind(this);

            this.boundHandleNavigationClick =
                this.handleNavigationClick.bind(this);

            this.previousFacetChangeHandler =
                this.facetManager?.onChange || null;
        }

        /**
         * Escribe mensajes en consola cuando debug está activo.
         */
        log(...args) {
            if (!this.debug) return;

            console.info(
                "[NostoPlpController]",
                ...args
            );
        }

        /**
         * Convierte un valor en una cadena limpia.
         */
        normalizeString(value) {
            return String(value ?? "").trim();
        }

        /**
         * Convierte un valor en un entero positivo.
         */
        normalizePositiveInteger(
            value,
            fallback
        ) {
            const number = Number(value);

            return Number.isFinite(number) &&
                number > 0
                ? Math.floor(number)
                : fallback;
        }

        /**
         * Resuelve un elemento desde un selector o nodo.
         */
        resolveElement(target) {
            if (target instanceof Element) {
                return target;
            }

            if (typeof target === "string") {
                return document.querySelector(
                    target
                );
            }

            return null;
        }

        getInfiniteScrollPreloadDistance() {
            const values =
                this.infiniteScrollRootMargin
                    .split(/\s+/)
                    .map((value) =>
                        parseInt(value, 10) || 0
                    );

            if (values.length === 4) {
                return values[2];
            }

            if (values.length === 2) {
                return values[0];
            }

            return values[0] || 2000;
        }

        /**
         * Valida que las dependencias compartidas estén disponibles.
         */
        validateDependencies() {
            const dependencies = {
                client: this.client,
                facetManager: this.facetManager,
                hitStore: this.hitStore,
                renderer: this.renderer,
            };

            for (const [
                name,
                dependency,
            ] of Object.entries(dependencies)) {
                if (!dependency) {
                    throw new Error(
                        `NostoPlpController requiere la dependencia "${name}".`
                    );
                }
            }

            if (!this.categoryId) {
                throw new Error(
                    "NostoPlpController requiere un categoryId."
                );
            }
        }

        /**
         * Inicializa el PLP, conecta eventos y carga los productos.
         */
        async init() {
            if (this.isInitialized) {
                return this;
            }

            this.validateDependencies();
            this.bindEvents();
            this.bindFacetManager();

            this.isInitialized = true;

            this.restoreSortFromUrl();

            const loadedTarget =
                this.restoreLoadedProducts
                    ? this.getLoadedTargetFromUrl()
                    : 0;

            if (loadedTarget > this.pageSize) {
                await this.restoreProductsUntil(
                    loadedTarget
                );
            } else {
                await this.loadProducts({
                    mode: "replace",
                    includeFacets: true,
                });
            }

            if (this.restoreScroll) {
                await this.restoreScrollPosition();
            }

            return this;
        }

        /**
         * Desconecta eventos y observadores del controller.
         */
        destroy() {
            this.latestRequestId += 1;

            this.sortElement?.removeEventListener(
                "change",
                this.boundHandleSortChange
            );

            global.removeEventListener(
                "scroll",
                this.boundHandleInfiniteScrollFallback
            );

            global.removeEventListener(
                "pagehide",
                this.boundHandlePageHide
            );

            document.removeEventListener(
                "click",
                this.boundHandleNavigationClick,
                true
            );

            this.observer?.disconnect();
            this.observer = null;

            if (this.facetManager) {
                this.facetManager.setChangeHandler(
                    this.previousFacetChangeHandler
                );
            }

            this.isInitialized = false;
        }

        /**
         * Conecta los eventos propios del PLP.
         */
        bindEvents() {
            this.sortElement?.addEventListener(
                "change",
                this.boundHandleSortChange
            );

            global.addEventListener(
                "pagehide",
                this.boundHandlePageHide
            );

            global.addEventListener(
                "scroll",
                this.boundHandleInfiniteScrollFallback,
                {
                    passive: true,
                }
            );

            document.addEventListener(
                "click",
                this.boundHandleNavigationClick,
                true
            );

            this.bindInfiniteScroll();
        }

        /**
         * Conecta el cambio de filtros con una nueva carga del PLP.
         */
        bindFacetManager() {
            this.facetManager.setChangeHandler(
                (detail) => {
                    if (
                        typeof this.previousFacetChangeHandler ===
                        "function"
                    ) {
                        this.previousFacetChangeHandler(
                            detail
                        );
                    }

                    if (this.isRestoring) return;

                    this.handleFacetChange(detail);
                }
            );
        }

        /**
         * Inicializa el observer utilizado para infinite scroll.
         */
        bindInfiniteScroll() {
            if (!this.loadMoreElement) {
                return;
            }

            this.observer?.disconnect();

            this.observer =
                new IntersectionObserver(
                    (entries) => {
                        const shouldLoad =
                            entries.some(
                                (entry) =>
                                    entry.isIntersecting
                            );

                        if (
                            shouldLoad &&
                            !this.isRestoring
                        ) {
                            this.loadMore();
                        }
                    },
                    {
                        rootMargin:
                            this.infiniteScrollRootMargin,
                    }
                );

            this.observer.observe(
                this.loadMoreElement
            );
        }

        handleInfiniteScrollFallback() {
            if (
                !this.loadMoreElement ||
                this.isLoading ||
                !this.hasMore ||
                this.isRestoring
            ) {
                return;
            }

            const currentScrollY =
                global.scrollY || 0;

            const isScrollingDown =
                currentScrollY > this.lastScrollY;

            this.lastScrollY =
                currentScrollY;

            if (!isScrollingDown) {
                return;
            }

            const rect =
                this.loadMoreElement
                    .getBoundingClientRect();

            const preloadDistance =
                this.getInfiniteScrollPreloadDistance();

            const triggerPoint =
                global.innerHeight +
                preloadDistance;

            /*
             * Si el sentinel ya está dentro del rango
             * o incluso quedó por encima debido a un
             * scroll muy rápido, cargamos igualmente.
             */
            if (
                rect.top <= triggerPoint
            ) {
                this.loadMore();
            }
        }

        /**
         * Devuelve la configuración de ordenamiento correspondiente al selector.
         */
        getSort() {
            const value =
                this.sortElement?.value || "";

            const sortMap = {
                "price-descending": {
                    field: "price",
                    order: "desc",
                },
                "price-ascending": {
                    field: "price",
                    order: "asc",
                },
                "created-descending": {
                    field: "datePublished",
                    order: "desc",
                },
            };

            return sortMap[value] || null;
        }

        restoreSortFromUrl() {
            if (
                !this.syncSortToUrl ||
                !this.sortElement
            ) {
                return;
            }

            const url = new URL(
                global.location.href
            );

            const value =
                this.normalizeString(
                    url.searchParams.get(
                        this.sortUrlParam
                    )
                );

            if (!value) {
                return;
            }

            const hasOption =
                Array.from(
                    this.sortElement.options || []
                ).some(
                    (option) =>
                        option.value === value
                );

            if (hasOption) {
                this.sortElement.value = value;
            }
        }

        setSortInUrl() {
            if (!this.syncSortToUrl) {
                return;
            }

            const url = new URL(
                global.location.href
            );

            if (this.getSort()) {
                url.searchParams.set(
                    this.sortUrlParam,
                    this.sortElement.value
                );
            } else {
                url.searchParams.delete(
                    this.sortUrlParam
                );
            }

            global.history.replaceState(
                global.history.state || {},
                "",
                url
            );
        }

        /**
         * Construye la sección products enviada a Nosto.
         */
        buildProductsRequest({
            from = this.offset,
            size = this.pageSize,
            includeFacets = true,
        } = {}) {
            const sort = this.getSort();

            const filters =
                this.facetManager
                    .buildNostoFilters();

            return {
                categoryId:
                    String(
                        this.categoryId
                    ),

                ...(this.categoryPath
                    ? {
                        categoryPath:
                            this.categoryPath,
                    }
                    : {}),

                fields: [
                    ...this.productFields,
                ],

                from,
                size,

                ...(includeFacets
                    ? { facets: ["*"] }
                    : {}),

                ...(sort ? { sort } : {}),

                ...(filters.length
                    ? { filter: filters }
                    : {}),
            };
        }

        /**
         * Construye la petición completa enviada a Nosto.
         */
        buildRequest(options = {}) {
            return {
                products:
                    this.buildProductsRequest(
                        options
                    ),
            };
        }

        /**
         * Construye las opciones de búsqueda enviadas a Nosto.
         */
        buildSearchOptions() {
            return {
                track: this.track,
                redirect: this.redirect,
            };
        }

        /**
 * Actualiza los estados visuales de carga del PLP.
 */
        setLoading(isLoading) {
            this.isLoading =
                Boolean(isLoading);

            const showResultsOverlay =
                this.isLoading &&
                this.loadingMode !== "append";

            this.renderer.setLoading(
                showResultsOverlay
            );

            if (this.loadMoreElement) {
                this.loadMoreElement.classList.toggle(
                    "is-loading",
                    this.isLoading
                );

                this.loadMoreElement.disabled =
                    this.isLoading;

                this.loadMoreElement.setAttribute(
                    "aria-busy",
                    String(this.isLoading)
                );
            }

            this.onLoadingChange?.(
                this.isLoading,
                this
            );
        }

        /**
         * Comprueba si una respuesta pertenece a la petición más reciente.
         */
        isLatestRequest(requestId) {
            return (
                Number(requestId) ===
                Number(this.latestRequestId)
            );
        }

        /**
         * Ejecuta una carga de productos y actualiza los helpers compartidos.
         */
        async loadProducts({
            mode = "append",
            from = this.offset,
            size = this.pageSize,
            includeFacets = true,
            updateUrl = true,
        } = {}) {
            if (this.isLoading) {
                return null;
            }

            if (
                mode === "append" &&
                !this.hasMore
            ) {
                return null;
            }

            const requestId =
                ++this.latestRequestId;

            this.loadingMode =
                mode;

            this.setLoading(true);

            try {
                const response =
                    await this.client.search(
                        this.buildRequest({
                            from,
                            size,
                            includeFacets,
                        }),
                        this.buildSearchOptions()
                    );

                if (
                    !this.isLatestRequest(
                        requestId
                    )
                ) {
                    return null;
                }

                const hits =
                    response.products.hits;

                if (mode === "replace") {
                    this.hitStore.replace(hits);
                } else {
                    this.hitStore.addMany(hits);
                }

                await this.renderer.render(
                    hits,
                    {
                        mode,
                        context: {
                            source: "category",
                            track: this.track,
                            categoryId:
                                this.categoryId,
                            categoryPath:
                                this.categoryPath,
                        },
                    }
                );

                if (
                    !this.isLatestRequest(
                        requestId
                    )
                ) {
                    return null;
                }

                this.offset = from + hits.length;
                this.total =
                    response.products.total;

                this.hasMore =
                    hits.length === size &&
                    (
                        this.total <= 0 ||
                        this.offset < this.total
                    );

                if (updateUrl) {
                    this.setLoadedTargetInUrl(
                        this.offset
                    );
                }

                this.updateLoadMoreVisibility();

                if (
                    includeFacets &&
                    response.products.facets.length
                ) {
                    this.onFacets?.(
                        response.products.facets,
                        {
                            response,
                            controller: this,
                            facetManager:
                                this.facetManager,
                        }
                    );
                }

                this.onResponse?.(
                    response,
                    {
                        mode,
                        controller: this,
                    }
                );

                return response;
            } catch (error) {
                if (
                    !this.isLatestRequest(
                        requestId
                    )
                ) {
                    return null;
                }

                if (
                    mode === "replace" ||
                    this.offset === 0
                ) {
                    this.renderer.renderError(
                        error
                    );
                } else {
                    this.renderer.renderAppendError(
                        error
                    );
                }

                this.updateLoadMoreVisibility();

                this.onError?.(
                    error,
                    this
                );

                return null;
            } finally {
                if (
                    this.isLatestRequest(
                        requestId
                    )
                ) {
                    this.setLoading(false);
                    this.loadingMode = "replace";
                }
            }
        }

        /**
         * Limpia el estado actual y ejecuta una nueva primera carga.
         */
        async resetAndLoad({
            includeFacets = true,
        } = {}) {
            this.latestRequestId += 1;
            this.offset = 0;
            this.total = null;
            this.hasMore = true;

            this.hitStore.clear();
            this.renderer.clear();

            this.setLoadedTargetInUrl(0);

            return this.loadProducts({
                mode: "replace",
                from: 0,
                size: this.pageSize,
                includeFacets,
            });
        }

        /**
         * Carga la siguiente página del PLP.
         */
        async loadMore() {
            if (
                this.isLoading ||
                !this.hasMore
            ) {
                return null;
            }

            return this.loadProducts({
                mode: "append",
                includeFacets: false,
            });
        }

        /**
 * Reacciona únicamente cuando el estado de filtros debe aplicarse.
 */
        async handleFacetChange(
            detail = {}
        ) {
            const allowedTypes = [
                "apply",
                "reset",
            ];

            if (
                !allowedTypes.includes(
                    detail.type
                )
            ) {
                return null;
            }

            return this.resetAndLoad({
                includeFacets: true,
            });
        }

        /**
         * Reacciona a un cambio en el ordenamiento.
         */
        async handleSortChange() {
            this.setSortInUrl();

            return this.resetAndLoad({
                includeFacets: true,
            });
        }

        /**
         * Actualiza la visibilidad del indicador de infinite scroll.
         */
        updateLoadMoreVisibility() {
            if (!this.loadMoreElement) {
                return;
            }

            this.loadMoreElement.hidden =
                !this.hasMore;

            this.loadMoreElement.classList.toggle(
                "is-hidden",
                !this.hasMore
            );
        }

        /**
         * Obtiene la cantidad de productos que debe restaurarse desde la URL.
         */
        getLoadedTargetFromUrl() {
            if (
                !this.syncPaginationToUrl
            ) {
                return 0;
            }

            const url = new URL(
                global.location.href
            );

            const value = Number(
                url.searchParams.get(
                    this.loadedUrlParam
                )
            );

            return Number.isFinite(value) &&
                value > 0
                ? Math.floor(value)
                : 0;
        }

        /**
         * Guarda en la URL la cantidad de productos cargados.
         */
        setLoadedTargetInUrl(value) {
            if (
                !this.syncPaginationToUrl
            ) {
                return;
            }

            const url = new URL(
                global.location.href
            );

            const normalizedValue =
                Number(value);

            if (
                !Number.isFinite(
                    normalizedValue
                ) ||
                normalizedValue <= 0
            ) {
                url.searchParams.delete(
                    this.loadedUrlParam
                );
            } else {
                url.searchParams.set(
                    this.loadedUrlParam,
                    String(
                        Math.floor(
                            normalizedValue
                        )
                    )
                );
            }

            global.history.replaceState(
                global.history.state || {},
                "",
                url
            );
        }

        /**
         * Restaura productos hasta alcanzar la cantidad indicada en la URL.
         */
        async restoreProductsUntil(
            targetLoaded
        ) {
            const target =
                this.normalizePositiveInteger(
                    targetLoaded,
                    this.pageSize
                );

            this.isRestoring = true;

            this.offset = 0;
            this.total = null;
            this.hasMore = true;

            this.hitStore.clear();
            this.renderer.clear();

            try {
                let mode = "replace";

                while (
                    this.offset < target &&
                    this.hasMore
                ) {
                    const remaining =
                        target - this.offset;

                    const size = Math.min(
                        this.maxRestoreChunkSize,
                        remaining
                    );

                    const response =
                        await this.loadProducts({
                            mode,
                            from: this.offset,
                            size,
                            includeFacets:
                                mode === "replace",
                            updateUrl: false,
                        });

                    if (
                        !response ||
                        response.products.hits
                            .length === 0
                    ) {
                        break;
                    }

                    mode = "append";
                }

                this.setLoadedTargetInUrl(
                    this.offset
                );
            } finally {
                this.isRestoring = false;
            }
        }

        /**
         * Guarda el scroll y la cantidad de productos cargados en history.state.
         */
        saveHistoryState() {
            if (!this.restoreScroll) {
                return;
            }

            const currentState =
                global.history.state || {};

            const currentScrollY =
                global.scrollY || 0;

            global.history.replaceState(
                {
                    ...currentState,

                    [this.historyStateKey]: {
                        scrollY:
                            currentScrollY,
                        loaded:
                            this.offset,
                        href:
                            global.location.href,
                        timestamp:
                            Date.now(),
                    },
                },
                "",
                global.location.href
            );
        }

        /**
         * Recupera el estado del PLP guardado en history.state.
         */
        getHistoryState() {
            return (
                global.history.state?.[
                this.historyStateKey
                ] || null
            );
        }

        /**
         * Guarda el estado cuando se abandona la página.
         */
        handlePageHide() {
            this.saveHistoryState();
        }

        /**
         * Guarda el estado antes de navegar hacia un producto.
         */
        handleNavigationClick(event) {
            const link =
                event.target?.closest?.(
                    "a[href]"
                );

            if (!link) return;

            const href =
                link.getAttribute("href") ||
                "";

            if (
                href.includes("/products/")
            ) {
                this.saveHistoryState();
            }
        }

        /**
         * Restaura la posición de scroll guardada anteriormente.
         */
        async restoreScrollPosition() {
            const state =
                this.getHistoryState();

            const target = Number(
                state?.scrollY
            );

            if (
                !Number.isFinite(target) ||
                target <= 0
            ) {
                return;
            }

            const startTime =
                performance.now();

            const maxWaitMs = 3000;

            const waitFrame = () =>
                new Promise((resolve) =>
                    requestAnimationFrame(resolve)
                );

            while (
                document.documentElement
                    .scrollHeight <
                target + global.innerHeight
            ) {
                if (
                    performance.now() -
                    startTime >
                    maxWaitMs
                ) {
                    break;
                }

                await waitFrame();
            }

            for (
                let attempt = 0;
                attempt < 5;
                attempt += 1
            ) {
                global.scrollTo(0, target);
                await waitFrame();
            }
        }

        /**
 * Registra el clic de un producto del PLP en Nosto.
 */
        async recordProductClick(
            productId,
            {
                fallbackHit = null,
            } = {}
        ) {
            const hit =
                this.hitStore.get(
                    productId
                ) ||
                fallbackHit;

            if (!hit) {
                this.log(
                    "No se encontró el hit para registrar el clic.",
                    productId
                );

                return false;
            }

            return this.client
                .recordSearchClick(
                    this.track,
                    hit
                );
        }

        /**
 * Registra el add to cart de un producto del PLP en Nosto.
 */
        async recordProductAddToCart(
            productId,
            {
                fallbackHit = null,
            } = {}
        ) {
            const hit =
                this.hitStore.get(
                    productId
                ) ||
                fallbackHit;

            if (!hit) {
                this.log(
                    "No se encontró el hit para registrar el add to cart.",
                    productId
                );

                return false;
            }

            return this.client
                .recordSearchAddToCart(
                    this.track,
                    hit
                );
        }
    }

    global.NostoPlpController =
        NostoPlpController;
})(window);

